import React, { useEffect, useMemo, useState } from 'react';
import ExportModal from '../tree/ExportModal';
import { psApi } from './psApi';
import { psNodes, psColumns } from './psExport';
import Spinner from '../ui/Spinner';
import '../bom-design-system.css';
import '../tree/BomTree.css';

/**
 * Export a product, a branch of it or a baseline with the BOM Viewer's own Export window —
 * format, Hierarchic / Summary, columns, export with files (DMS files + attachments zip).
 * The tree is read once from `export-rows` and handed over ready, so nothing walks Orcanos.
 * Wrapped in `.bom-scope`: every Export-window rule is scoped there (Note #63).
 */
export default function PsExportModal({ root, branch, baseline, onClose, onAuth }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let off = false;
    psApi.exportRows({ root, branch: branch || undefined, baseline: baseline || undefined })
      .then((d) => { if (!off) setData(d); })
      .catch((e) => { if (!off && !onAuth?.(e)) setError(e.message); });
    return () => { off = true; };
  }, [root, branch, baseline, onAuth]);

  const nodes = useMemo(() => (data ? psNodes(data.rows) : null), [data]);
  const columns = useMemo(() => (nodes ? psColumns(nodes, { baseline: data.baseline }) : null), [nodes, data]);

  if (!nodes) {
    return (
      <div className="bom-scope">
        <div className="modal-overlay" onClick={onClose}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Export</h2>
              <button className="modal-close" onClick={onClose} aria-label="Close">✕</button>
            </div>
            <div className="modal-body">
              {error
                ? <span className="export-error" role="alert">{error}</span>
                : <span className="export-progress-label"><Spinner /> Reading the structure…</span>}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bom-scope">
      <ExportModal
        nodes={nodes}
        columns={columns}
        name={data.name}
        prefKey="ps_export_cols"
        note={data.baseline
          ? 'Key, name, revision, status, quantity, find no. and ref. des. are as frozen in the baseline; other fields are read from Orcanos now.'
          : undefined}
        onClose={onClose}
      />
    </div>
  );
}
